import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AvForm } from "availity-reactstrap-validation";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { detailedDiff, diff } from "deep-object-diff";
import { uuid } from "uuidv4";

import {
    CustomCardBody,
    CustomCardFooter,
} from "@components/Common/CustomCardStyled";
import { store } from "@components/VerticalLayout/store";
import {
    store as saleSupportStore,
    setFranchiseDataIsUpdated,
    setIsInlineErrorCleared,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setIsLoading,
    setKey,
} from "../store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { languages, TOAST_LIMIT_DEFAULT } from "@constants/index";
import {
    CreateDataSaleSupport,
    advancedDisplayNameDefault,
    saleSupportFormType,
    translatedSaleSupport,
} from "./helper";
import SalesSupportModals from "./SalesSupportModals";
import SalesSupportHeader from "./SalesSupportHeader";
import SalesSupportSelect from "./SalesSupportSelect";
import DisplayNameField from "./DisplayNameField";
import TagsSelection from "./TagsSelection";
import FormFooterButtons from "./FormFooterButtons";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { GetSalesModesTypesAndTagsV2 } from "@pages/SettingGeneral/ModeOfSale/ModeOfSales/helper";
import { useFetchGlobal } from "../../../../api/hooks";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import "antd/dist/antd.css";

export default function SaleSupportForm({
    allDataShops,
    isAddSaleSupportClicked,
    setIsAddSaleSupportClicked,
    saleSupport,
    isAddNewSaleSupportButtonClicked,
    setSaleSupportData,
    userData,
    setLocalFilterActive,
    setLimit,
    setIsConsult,
    isConsult,
    dataState,
    SalesModesTypesAndTag,
    setSalesModesTypesAndTag,
}: saleSupportFormType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const { userID, franchiseID, shopID, oneShop, operatorID } = useSnapshot(
        store
    );
    const {
        shopsSaleSupportData,
        saleSupportData,
        isInlineErrorCleared,
        listFranchiseArchived,
        archivedData,
        isInputUpdated,
        isActionsClicked,
        isEdited,
    } = useSnapshot(saleSupportStore);
    const shopId = oneShop ? shopID : "0";
    const [isNameExist, setIsNameExist] = React.useState<boolean>(false);
    const [displayName, setDisplayName] = React.useState<string>("");
    const [saleSupportLabel, setSaleSupportLabel] = React.useState<string>("");
    const [title, setTitle] = React.useState<string>("");
    const [identifier, setIdentifier] = React.useState<string>("");
    const [inputSaleSupportLabel, setInputSaleSupportLabel] = React.useState<
        boolean
    >(false);
    const [inputDisplayName, setInputDisplayName] = React.useState<boolean>(
        false
    );
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const [tags, setTags] = React.useState<any[]>([]);
    const [generalSalesSupports, setGeneralSalesSupports] = React.useState<
        any[]
    >([]);
    const [tagsData, setTagsData] = React.useState<any>([]);
    const [isSendingTag, setIsSendingTag] = React.useState<boolean>(false);

    const [isMessageModalOpened, setIsMessageModalOpened] = React.useState<
        boolean
    >(false);
    const [idsAssociated, setIdsAssociated] = React.useState<any>([]);
    const [existingSaleSupport, setExistingSaleSupport] = React.useState<any>(
        {}
    );
    const [
        isMessageValidateClicked,
        setIsMessageValidateClicked,
    ] = React.useState<boolean>(false);
    const [
        isSaleSupportAssociatedModified,
        setIsSaleSupportAssociatedModified,
    ] = React.useState<boolean>(false);
    const [
        isArchivedFranchiseModalOpened,
        setIsArchivedFranchiseModalOpened,
    ] = React.useState<boolean>(false);
    const [
        isValidateButtonDisabled,
        setIsValidateButtonDisabled,
    ] = React.useState<boolean>(false);
    const [
        isAdvancedDisplayNameModalOpen,
        setIsAdvancedDisplayNameModalOpen,
    ] = React.useState(false);
    const [advancedDisplayName, setAdvancedDisplayName] = React.useState<any>(
        advancedDisplayNameDefault
    );
    const [isArchived, setIsArchived] = React.useState<boolean>(false);
    const [language, setLanguage] = React.useState<string[]>([]);
    const selectedLanguage: any = languages.find(
        (el: any) => el.value === i18nextLng
    );

    const uuidUser = localStorage.getItem("uuidUser");

    const addSaleSupport = React.useCallback(async () => {
        toast.dismiss();
        setLimit(TOAST_LIMIT_DEFAULT);
        try {
            const selectedSupport = generalSalesSupports.find(
                (elt: any) => elt.label === saleSupportLabel
            );

            if (!selectedSupport) {
                throw new Error("Invalid sale support label");
            }

            const id = selectedSupport.value;

            const dataSaleSupport = CreateDataSaleSupport(
                oneShop,
                isMessageValidateClicked,
                allDataShops,
                userID,
                franchiseID,
                operatorID,
                uuidUser,
                id,
                displayName,
                tagsData,
                saleSupportLabel,
                advancedDisplayName,
                shopId,
                saleSupportData
            );

            const requestOptions: RequestInit = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataSaleSupport),
            };

            const data: any = await fetchGlobal(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport`,
                requestOptions
            );

            if (data.error) {
                throw new Error("API returned an error");
            }

            showSuccessToast(`${t("Sale support is successfully added")!}`);
            setIsSendingTag(true);
            setIsEdited(false);
            setLocalFilterActive(false);
            setTagsData([]);
            setDisplayName("");
            setSaleSupportLabel("");
            clearData();
            clearMultiCustomSelectionData();
            setSaleSupportData([]);
            setAdvancedDisplayName(advancedDisplayNameDefault);
            setIsLoading(false);
            setKey(uuid());
            const configs = await GetSalesModesTypesAndTagsV2(
                franchiseID,
                shopId,
                fetchGlobal
            );
            const saleSupport = configs[0];

            if (!saleSupport) return;
            setSalesModesTypesAndTag(saleSupport);
        } catch (e) {
            console.error("Error in addSaleSupport:", e);
            ErrorToast(`${t("There's an error")!}`);
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        oneShop,
        isMessageValidateClicked,
        userID,
        shopId,
        franchiseID,
        identifier,
        displayName,
        tagsData,
        saleSupportLabel,
        t,
        setIsEdited,
        setLocalFilterActive,
        setSaleSupportData,
        setLimit,
    ]);

    async function editSaleSupport() {
        setLimit(TOAST_LIMIT_DEFAULT);
        try {
            const idsAssociated: any[] = [];
            const idsShopsAssociated: any[] = [];

            shopsSaleSupportData.forEach((el: any) => {
                el.subRows.forEach((element: any) => {
                    idsAssociated.push(element.Id);
                    idsShopsAssociated.push(el.shopId);
                });
            });
            setIdsAssociated([...new Set(idsShopsAssociated)]);
            const dataSaleSupport = [
                {
                    userId: userID,
                    shopId: [shopId],
                    franchiseId: franchiseID,
                    operatorId: operatorID,
                    uuidUser: uuidUser,
                    data: {
                        [identifier]: {
                            id: identifier,
                            displayName: displayName,
                            tag: tagsData.join(","),
                            isArchived: saleSupport.isArchived,
                            state: saleSupport.state,
                            shopId: shopId,
                            isActive:
                                Object.keys(dataState).length === 0
                                    ? saleSupport.isActive
                                    : saleSupport.Id === dataState.id
                                    ? dataState.state
                                    : saleSupport.isActive,
                            support_de_vente: saleSupportLabel,
                            isFavorite: saleSupport.isFavorite,
                            advancedDisplayName: advancedDisplayName,
                        },
                    },
                },
            ];
            if (userData.shopId === "0" && idsAssociated.includes(identifier)) {
                setIsSaleSupportAssociatedModified(true);
                setIsLoading(false);
                return;
            } else {
                const requestOptions: RequestInit = {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(dataSaleSupport),
                };

                const data: any = await fetchGlobal(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/saleSupport`,
                    requestOptions
                );

                if (data.error) {
                    throw new Error("API returned an error");
                }

                showSuccessToast(
                    `${t("The sale support is changed successfully")!}`
                );

                setIsSendingTag(true);
                setLocalFilterActive(false);
                setIsEdited(false);
                setDisplayName("");
                setTagsData([]);
                setSaleSupportLabel("");
                clearData();
                clearMultiCustomSelectionData();
                setSaleSupportData([]);
                setIsAddSaleSupportClicked!(!isAddSaleSupportClicked);
                setIsLoading(false);
                setKey(uuid());
            }
        } catch (e) {
            ErrorToast(`${t("There's an error")!}`);
            setIsLoading(false);
        }
    }

    const formatCreateLabelTag = (inputValue: any) => (
        <span>
            {inputValue} ({t("New tag")})
        </span>
    );

    function handleValidateButtonOnClickEvent() {
        setLimit(TOAST_LIMIT_DEFAULT);
        setIsValidateButtonDisabled(true);
        if (
            saleSupport?.Id !== undefined &&
            saleSupport.advancedDisplayName === advancedDisplayName &&
            Object.keys(
                detailedDiff(
                    saleSupport.Tag.split(",").filter((x: any) => x !== ""),
                    tagsData
                ).updated
            ).length === 0 &&
            Object.keys(
                detailedDiff(
                    saleSupport.Tag.split(",").filter((x: any) => x !== ""),
                    tagsData
                ).added
            ).length === 0 &&
            Object.keys(
                detailedDiff(
                    saleSupport.Tag.split(",").filter((x: any) => x !== ""),
                    tagsData
                ).deleted
            ).length === 0 &&
            saleSupport[t("Sale support")] === saleSupportLabel
        ) {
            setIsEdited(false);
            setIsModalOpened(true);
        } else {
            if (saleSupportLabel === "") {
                setInputSaleSupportLabel(true);
            }

            if (advancedDisplayName.languages[i18nextLng] === "") {
                setInputDisplayName(true);
            }

            if (isNameExist) {
                setIsValidateButtonDisabled(false);
            }

            if (saleSupport.Id) {
                let sameSaleSupport: any = allDataShops.find(
                    (el: any) => el[t("Sale support")] === saleSupportLabel
                );

                let isTagNotModified: any =
                    sameSaleSupport !== undefined &&
                    sameSaleSupport?.Tag === tagsData.join(",");

                if (
                    saleSupport.Id !== undefined &&
                    saleSupportLabel !== "" &&
                    advancedDisplayName.languages[i18nextLng].trim() !== "" &&
                    !isNameExist
                ) {
                    if (
                        isEdited === false &&
                        isTagNotModified &&
                        Object.keys(
                            diff(
                                saleSupport.advancedDisplayName,
                                advancedDisplayName
                            )
                        ).length === 0
                    ) {
                        setIsModalOpened(!isModalOpened);
                    } else {
                        editSaleSupport();
                    }
                }
            } else if (
                saleSupportLabel !== "" &&
                advancedDisplayName.languages[i18nextLng].trim() !== "" &&
                !isNameExist
            ) {
                let isSaleSupportExistArchivedFranchise = listFranchiseArchived.find(
                    (el: any) => el[t("Sale support")] === saleSupportLabel
                );
                let isSaleSupportExist: boolean = false;

                if (allDataShops !== undefined) {
                    isSaleSupportExist =
                        allDataShops.find(
                            (el: any) =>
                                el[t("Sale support")] === saleSupportLabel
                        ) !== undefined;
                }

                if (isSaleSupportExistArchivedFranchise !== undefined) {
                    setIsMessageModalOpened(true);
                    setIsArchived(true);
                } else if (allDataShops !== undefined && isSaleSupportExist) {
                    setIdentifier(
                        // eslint-disable-next-line
                        allDataShops.find((el: any) => {
                            if (el[t("Sale support")] === saleSupportLabel)
                                return el.Id;
                        }).Id
                    );
                    setIsMessageModalOpened(true);
                    setIsArchived(false);
                } else {
                    addSaleSupport();
                }
            }
        }
        setTimeout(() => {
            setIsValidateButtonDisabled(false);
        }, 500);
    }

    const checkIfSaleSupportExist = React.useCallback(() => {
        let allSaleSupport = [...saleSupportData, ...archivedData];
        return allSaleSupport.some(
            (element) => element[t("Sale support")] === saleSupportLabel
        );
    }, [archivedData, saleSupportData, saleSupportLabel, t]);

    useEffect(() => {
        if (saleSupport.Id) {
            setIdentifier(saleSupport.Id);
            setTitle(
                t(isConsult ? "View a sale support" : "Modify a sale support")
            );
            setDisplayName(saleSupport[t("Display name")]);
            setTagsData(saleSupport.Tag.split(","));
            setSaleSupportLabel(saleSupport[t("Sale support")]);
            setAdvancedDisplayName(saleSupport.advancedDisplayName);
        } else {
            setDisplayName("");
            setTitle(t("Add a sale support"));
            setSaleSupportLabel("");
            setIdentifier("");
            setTagsData([]);
            setAdvancedDisplayName(advancedDisplayNameDefault);
        }
        let data = (SalesModesTypesAndTag.saleSupport || []).map(
            (element: any) => ({
                ...element,
                label: t(element.label),
            })
        );
        const uniqueData = Array.from(
            new Map(data.map((item: any) => [item.value, item])).values()
        );
        setGeneralSalesSupports(uniqueData);
        setTags(SalesModesTypesAndTag.tags || []);
        setLanguage(SalesModesTypesAndTag.languages || []);
        // eslint-disable-next-line
    }, [
        saleSupport,
        t,
        isAddNewSaleSupportButtonClicked,
        SalesModesTypesAndTag,
    ]);

    useEffect(() => {
        if (isInlineErrorCleared === true) {
            setInputDisplayName(false);
            setIsInlineErrorCleared(false);
            clearData();
            clearMultiCustomSelectionData();
            setTagsData([]);
            setGeneralSalesSupports([]);
        }
    }, [isInlineErrorCleared]);

    useEffect(() => {
        clearData();
        clearMultiCustomSelectionData();
    }, [isAddSaleSupportClicked, saleSupport.Id]);

    useEffect(() => {
        setInputDisplayName(false);
        setIsInlineErrorCleared(false);
    }, [isInputUpdated]);
    useEffect(() => {
        if (advancedDisplayName.languages[i18nextLng] !== "") {
            setInputDisplayName(false);
        }
        // eslint-disable-next-line
    }, [advancedDisplayName, i18nextLng]);

    useEffect(() => {
        let isSaleSupportExist = checkIfSaleSupportExist();
        if (
            isSaleSupportExist &&
            (saleSupport.Id === undefined ||
                (saleSupport.Id !== undefined &&
                    saleSupport[t("Sale support")] !== saleSupportLabel))
        ) {
            setIsNameExist(true);
        } else {
            setIsNameExist(false);
        }
    }, [
        saleSupportLabel,
        t,
        displayName,
        checkIfSaleSupportExist,
        saleSupport,
    ]);

    React.useEffect(() => {
        let allSaleShop: any = [];
        if (shopsSaleSupportData !== undefined) {
            shopsSaleSupportData.forEach((el: any) => {
                if (el.subRows !== undefined)
                    el.subRows.forEach((element: any) => {
                        allSaleShop.push(element);
                    });
            });
        }
        setExistingSaleSupport(
            allSaleShop.find((el: any) => el.Id === identifier)
        );
    }, [identifier, shopsSaleSupportData]);

    React.useEffect(() => {
        if (isMessageValidateClicked === true) {
            addSaleSupport();
            setIsMessageModalOpened(false);
        }
        setIsMessageValidateClicked(false);
    }, [
        allDataShops,
        userData,
        isMessageValidateClicked,
        addSaleSupport,
        t,
        saleSupportLabel,
        tagsData,
        saleSupport,
    ]);
    const onClose = () => {
        setIsConsult(false);
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setSaleSupportData([]);
                setIsAddSaleSupportClicked!(!isAddSaleSupportClicked);
                clearData();
                clearMultiCustomSelectionData();
            });
        } else {
            setSaleSupportData([]);
            setIsAddSaleSupportClicked!(!isAddSaleSupportClicked);
            clearData();
            clearMultiCustomSelectionData();
        }
    };

    const onCancel = () => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setSaleSupportData([]);
                setIsAddSaleSupportClicked(!isAddSaleSupportClicked);
                clearData();
                clearMultiCustomSelectionData();
            });
        } else {
            setSaleSupportData([]);
            setIsAddSaleSupportClicked(!isAddSaleSupportClicked);
            clearData();
            clearMultiCustomSelectionData();
        }
        setIsConsult(false);
    };
    return (
        <React.Fragment>
            <AvForm className="card">
                <SalesSupportHeader title={title} onClose={onClose} />
                <CustomCardBody
                    className={`pl-3 pr-5 `}
                    style={{
                        maxHeight: "470px",
                        overflowY: " auto",
                    }}
                >
                    <SalesSupportSelect
                        saleSupportLabel={saleSupportLabel}
                        setSaleSupportLabel={setSaleSupportLabel}
                        saleSupport={saleSupport}
                        generalSalesSupports={generalSalesSupports}
                        translatedSaleSupport={translatedSaleSupport}
                        displayName={displayName}
                        setDisplayName={setDisplayName}
                        setAdvancedDisplayName={setAdvancedDisplayName}
                        advancedDisplayName={advancedDisplayName}
                        advancedDisplayNameDefault={advancedDisplayNameDefault}
                        isConsult={isConsult}
                        isNameExist={isNameExist}
                        inputSaleSupportLabel={inputSaleSupportLabel}
                        setInputSaleSupportLabel={setInputSaleSupportLabel}
                        setInputDisplayName={setInputDisplayName}
                        setIsEdited={setIsEdited}
                    />
                    <DisplayNameField
                        isConsult={isConsult}
                        i18nextLng={i18nextLng}
                        selectedLanguage={selectedLanguage}
                        advancedDisplayName={advancedDisplayName}
                        setAdvancedDisplayName={setAdvancedDisplayName}
                        advancedDisplayNameDefault={advancedDisplayNameDefault}
                        saleSupportLabel={saleSupportLabel}
                        setDisplayName={setDisplayName}
                        setIsEdited={setIsEdited}
                        inputDisplayName={inputDisplayName}
                        setInputDisplayName={setInputDisplayName}
                        isAdvancedDisplayNameModalOpen={
                            isAdvancedDisplayNameModalOpen
                        }
                        setIsAdvancedDisplayNameModalOpen={
                            setIsAdvancedDisplayNameModalOpen
                        }
                    />
                    <TagsSelection
                        isConsult={isConsult}
                        tagsData={tagsData}
                        setTagsData={setTagsData}
                        tags={tags || []}
                        formatCreateLabelTag={formatCreateLabelTag}
                        isSendingTag={isSendingTag}
                        setIsEdited={setIsEdited}
                    />
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <FormFooterButtons
                        isValidateButtonDisabled={isValidateButtonDisabled}
                        onCancel={onCancel}
                        onValidate={handleValidateButtonOnClickEvent}
                        isConsult={isConsult}
                    />
                </CustomCardFooter>
            </AvForm>
            <SalesSupportModals
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isAddSaleSupportClicked={isAddSaleSupportClicked}
                setIsAddSaleSupportClicked={setIsAddSaleSupportClicked}
                setSaleSupportData={setSaleSupportData}
                setFranchiseDataIsUpdated={setFranchiseDataIsUpdated}
                isSaleSupportAssociatedModified={
                    isSaleSupportAssociatedModified
                }
                setIsSaleSupportAssociatedModified={
                    setIsSaleSupportAssociatedModified
                }
                isMessageModalOpened={isMessageModalOpened}
                setIsMessageModalOpened={setIsMessageModalOpened}
                isArchived={isArchived}
                setIsMessageValidateClicked={setIsMessageValidateClicked}
                isArchivedFranchiseModalOpened={isArchivedFranchiseModalOpened}
                setIsArchivedFranchiseModalOpened={
                    setIsArchivedFranchiseModalOpened
                }
                isAdvancedDisplayNameModalOpen={isAdvancedDisplayNameModalOpen}
                setIsAdvancedDisplayNameModalOpen={
                    setIsAdvancedDisplayNameModalOpen
                }
                advancedDisplayName={advancedDisplayName}
                setAdvancedDisplayName={setAdvancedDisplayName}
                language={language}
                userData={userData}
                shopId={shopId}
                idsAssociated={idsAssociated}
                existingSaleSupport={existingSaleSupport}
                identifier={identifier}
                saleSupportLabel={saleSupportLabel}
                tagsData={tagsData}
                i18nextLng={i18nextLng}
                isConsult={isConsult}
                isActionsClicked={isActionsClicked}
                setActions={setActions}
                setIsActionsClicked={setIsActionsClicked}
                setIsEdited={setIsEdited}
                clearData={clearData}
                clearMultiCustomSelectionData={clearMultiCustomSelectionData}
                setTagsData={setTagsData}
                saleSupport={saleSupport}
            />
        </React.Fragment>
    );
}
